/**
 * useInsolationModePreview — plays a one-shot sun-sweep animation on the
 * first entry to Insolation mode within the current page session.
 *
 * Sequence: 06:00 → 18:00 → smooth return → 12:00.
 *
 * Implementation note: two-phase lerp — forward (min→max) in `steps` ticks,
 * then return (max→noon) in proportionally fewer ticks — both at `intervalMs`
 * so the sun moves at a constant apparent speed throughout.
 *
 * @param {object}  params
 * @param {boolean}             params.enabled            — gate (SVG mounted, data ready)
 * @param {boolean}             params.userInteracted     — flips true on first slider touch
 * @param {(deg: number) => void} params.setInsolationAngleDeg
 * @param {number}              params.minAngleDeg        — INSOLATION_DEFAULTS.minAngleDeg (06:00)
 * @param {number}              params.maxAngleDeg        — INSOLATION_DEFAULTS.maxAngleDeg (18:00)
 * @param {number}              params.noonAngleDeg       — INSOLATION_DEFAULTS.initialAngleDeg (12:00)
 * @param {number}              [params.steps=24]         — ticks across the forward sweep
 * @param {number}              [params.intervalMs=60]    — ms per tick (24 * 60ms ≈ 1.44s forward)
 */

import { useEffect, useRef } from 'react'

let previewPlayedThisSession = false

export function __clearInsolationPreviewMemoryForTests() {
  previewPlayedThisSession = false
}

export function useInsolationModePreview({
  enabled,
  userInteracted,
  setInsolationAngleDeg,
  minAngleDeg,
  maxAngleDeg,
  noonAngleDeg,
  steps = 24,
  intervalMs = 60,
}) {
  const setAngleRef         = useRef(setInsolationAngleDeg)
  setAngleRef.current       = setInsolationAngleDeg
  const userInteractedRef   = useRef(userInteracted)
  userInteractedRef.current = userInteracted

  useEffect(() => {
    if (!enabled) return
    if (userInteracted) return
    if (previewPlayedThisSession) return

    previewPlayedThisSession = true

    let cancelled = false
    let timer = null

    // Phase 2: max → noon (proportional steps, same intervalMs = constant speed).
    const runReturn = (i, stepsReturn) => {
      if (cancelled || userInteractedRef.current) return
      if (i > stepsReturn) return  // done

      const t = i / stepsReturn
      const angle = maxAngleDeg + t * (noonAngleDeg - maxAngleDeg)
      setAngleRef.current(angle)

      timer = window.setTimeout(() => runReturn(i + 1, stepsReturn), intervalMs)
    }

    // Phase 1: min → max.
    const runSweep = (i) => {
      if (cancelled || userInteractedRef.current) return
      if (i > steps) {
        // Forward sweep done — start return sweep with proportional step count.
        const range = Math.max(1, maxAngleDeg - minAngleDeg)
        const stepsReturn = Math.max(1, Math.round(((maxAngleDeg - noonAngleDeg) / range) * steps))
        timer = window.setTimeout(() => runReturn(0, stepsReturn), intervalMs)
        return
      }

      const t = i / steps  // 0..1
      const angle = minAngleDeg + t * (maxAngleDeg - minAngleDeg)
      setAngleRef.current(angle)

      timer = window.setTimeout(() => runSweep(i + 1), intervalMs)
    }

    // Small initial delay so the mode is visually settled before the sun moves.
    timer = window.setTimeout(() => runSweep(0), intervalMs * 2)

    return () => {
      cancelled = true
      if (timer != null) window.clearTimeout(timer)
    }
  }, [enabled, userInteracted, minAngleDeg, maxAngleDeg, noonAngleDeg, steps, intervalMs])
}
